import {
    FormProvider,
    RHFSelect,
    RHFTextField,
} from "@convin/components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { alpha, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { Control, Controller, useForm, UseFormReturn } from "react-hook-form";
import * as yup from "yup";
import useNodeContext from "../../hooks/useNodeContext";
import { useGetDomainConfigQuery } from "@convin/redux/services/domain/domain.service";
import { useGetCallTypesQuery } from "@convin/redux/services/settings/callTypeManager.service";
import { useGetCallTagsQuery } from "@convin/redux/services/settings/callTagsManager.service";
import { useReactFlowStateContextProvider } from "../../hooks/useReactFlowStateContextProvider";
import {
    AttributeBlockFormType,
    SingleResponseNode,
} from "../../../types/bert";
import { isDefined } from "@convin/utils/helper/common.helper";
import BlockHeader from "./BlockHeader";
import GenericMultipleSelect from "@convin/components/select/GenericMultipleSelect";
import { CallTag, CallType } from "@convin/type/CallManager";
import CustomSkeletonLoader from "@convin/components/custom_components/Skeleton";
import { attribute_types } from "../../constants";
import {
    useGetDispositionByTemplateIdQuery,
    useGetDispositionTemplateListQuery,
    useGetLevelsByDispositionIdQuery,
} from "@convin/redux/services/settings/aiDisposition.service";
import GenericSingleSelect from "@convin/components/select/GenericSingleSelect";
import PlusIconSvg from "@convin/components/svg/CI/PlusIconSvg";
import { useState } from "react";
import useParameterAiConfigContext from "../../hooks/useParameterAiConfigContext";
import RHFSelectCustom from "@convin/components/hook-form/RHFSelectCustom";
import { useGetCustomEntitiesQuery } from "@convin/redux/services/settings/entity.service";

export default function AttributeBlock({
    showCreateButton,
}: {
    showCreateButton?: boolean;
}) {
    const { createSingleResponseNode, updateSingleResponseNode } =
        useReactFlowStateContextProvider();
    const {
        sourceId,
        direction,
        onNodeSaveCallBack,
        nodeDataToUpdate,
        nodeIdToUpdate,
        blockParentId,
    } = useNodeContext();
    const { data: domainConfig } = useGetDomainConfigQuery();
    const meetingTypeOptions =
        domainConfig?.getConversationTypeOptions().map((type) => ({
            id: `${type.id}`,
            label: type.label,
        })) ?? [];

    const schema = yup.object().shape({
        name: yup.string().required("Name is Required"),
        attribute_type: yup.string().required("Attribute type is Required"),
        attribute: yup
            .array()
            .of(yup.mixed())
            .nullable()
            .when("attribute_type", {
                is: (attribute_type: string) =>
                    !["ai_disposition", "entity"].includes(attribute_type),
                then: (schema) =>
                    schema
                        .required("Attribute is Required")
                        .min(1, "Select at least one attribute"),
                otherwise: (schema) => schema.notRequired(),
            }),
        ai_disposition: yup
            .number()
            .nullable()
            .when("attribute_type", {
                is: "ai_disposition",
                then: (schema) => schema.required("AI Disposition is Required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        disposition_levels: yup.object().when("attribute_type", {
            is: "ai_disposition",
            then: (schema) =>
                schema.shape({
                    level1: yup.number().required("Level 1 is Required"),
                    level2: yup.number().nullable(),
                    level3: yup.number().nullable(),
                }),
            otherwise: (schema) =>
                schema.shape({
                    level1: yup.number().nullable(),
                    level2: yup.number().nullable(),
                    level3: yup.number().nullable(),
                }),
        }),
        attribute_entity: yup
            .number()
            .nullable()
            .when("attribute_type", {
                is: "entity",
                then: (schema) => schema.required("Entity is Required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        eligible_entities: yup
            .array()
            .of(yup.string())
            .nullable()
            .when("attribute_type", {
                is: "entity",
                then: (schema) =>
                    schema
                        .required("Selected Eligible Values is Required")
                        .min(1, "Select at least one eligible value"),
                otherwise: (schema) => schema.notRequired(),
            }),
    });

    const methods = useForm<AttributeBlockFormType>({
        resolver: yupResolver(schema),
        values: isDefined(nodeDataToUpdate)
            ? { ...(nodeDataToUpdate as AttributeBlockFormType) }
            : {
                  name: "",
                  attribute_type: "conversation_type",
                  attribute: [],
                  ai_disposition: null,
                  disposition_levels: {
                      level1: null,
                      level2: null,
                      level3: null,
                  },
                  attribute_entity: null,
                  eligible_entities: [],
              },
    });
    const currentNodeData = nodeDataToUpdate as AttributeBlockFormType;
    const { watch, setValue } = methods;
    const attribute_type = watch("attribute_type");
    const { data: callTypes, isLoading: isTypesLoading } = useGetCallTypesQuery(
        undefined,
        {
            skip: attribute_type !== "type",
        }
    );
    const { data: callTags, isLoading: isTagsLoading } = useGetCallTagsQuery(
        undefined,
        {
            skip: attribute_type !== "tags",
        }
    );

    const { handleSubmit, control, getValues } = methods;

    const onSubmit = async (values: AttributeBlockFormType) => {
        if (isDefined(nodeIdToUpdate)) {
            updateSingleResponseNode<AttributeBlockFormType>({
                data: values,
                sourceId: nodeIdToUpdate,
                blockParentId,
            });
        } else
            createSingleResponseNode<SingleResponseNode<"attribute">>({
                data: {
                    type: "attribute",
                    is_not: false,
                    metadata: values,
                },
                sourceId,
                direction,
                blockParentId,
            });
        onNodeSaveCallBack();
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <BlockHeader
                title={"Attribute"}
                showCreateButton={showCreateButton}
            />
            <Box
                sx={{
                    width: "100%",
                }}
            >
                <Stack gap={1.5}>
                    <RHFTextField
                        name="name"
                        className="w-full"
                        variant="outlined"
                        size="small"
                        placeholder="Block Name"
                        label="Block Name"
                    />
                    <RHFSelect
                        name="attribute_type"
                        className="w-full nodrag nopan"
                        variant="outlined"
                        size="small"
                        label="Select Attribute Type"
                        options={attribute_types}
                        onChangeCallback={() => {
                            setValue("attribute", []);
                            setValue("ai_disposition", null);
                            setValue("disposition_levels", {
                                level1: null,
                                level2: null,
                                level3: null,
                            });
                            setValue("attribute_entity", null);
                            setValue("eligible_entities", []);
                        }}
                    />
                    {["tags", "type", "conversation_type"].includes(
                        attribute_type
                    ) ? (
                        isTagsLoading || isTypesLoading ? (
                            <CustomSkeletonLoader rows={2} />
                        ) : (
                            <Controller
                                name="attribute"
                                control={control}
                                render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                }) => (
                                    <GenericMultipleSelect<
                                        | CallTag
                                        | CallType
                                        | (typeof meetingTypeOptions)[0]
                                    >
                                        label="Select Attribute"
                                        autocompleteProps={{
                                            size: "small",
                                            options: attribute_type
                                                ? attribute_type ===
                                                  "conversation_type"
                                                    ? meetingTypeOptions
                                                    : attribute_type === "tags"
                                                    ? callTags ?? []
                                                    : callTypes ?? []
                                                : [],
                                            className: "nowheel nodrag nopan",
                                        }}
                                        values={value ?? []}
                                        setValues={(value) => onChange(value)}
                                        className="w-full nowheel nodrag nopan"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        )
                    ) : null}
                    {attribute_type === "ai_disposition" && (
                        <AiDisposition
                            methods={methods}
                            control={control}
                            defaultShowLevel2={
                                !!currentNodeData?.disposition_levels?.level2 &&
                                !!getValues("disposition_levels.level2")
                            }
                            defaultShowLevel3={
                                !!currentNodeData?.disposition_levels?.level3 &&
                                !!getValues("disposition_levels.level3")
                            }
                        />
                    )}
                    {attribute_type === "entity" && (
                        <Entity methods={methods} control={control} />
                    )}
                </Stack>
            </Box>
        </FormProvider>
    );
}

const AiDisposition = ({
    methods,
    control,
    defaultShowLevel2,
    defaultShowLevel3,
}: {
    methods: UseFormReturn<AttributeBlockFormType>;
    control: Control<AttributeBlockFormType>;
    defaultShowLevel2: boolean;
    defaultShowLevel3: boolean;
}) => {
    const { data: aiDispositionTemplates } = useGetDispositionTemplateListQuery(
        {
            isArchive: false,
        }
    );
    const [showLevel2, setShowLevel2] = useState(defaultShowLevel2);
    const [showLevel3, setShowLevel3] = useState(defaultShowLevel3);
    const { watch, setValue } = methods;
    const ai_disposition = watch("ai_disposition");
    const selectedLevel1 = watch("disposition_levels.level1");
    const selectedLevel2 = watch("disposition_levels.level2");
    const selectedLevel3 = watch("disposition_levels.level3");

    const { data: level1, isLoading: isLoadingLevel1 } =
        useGetDispositionByTemplateIdQuery(ai_disposition!, {
            skip: !ai_disposition,
        });
    const { data: level2, isLoading: isLoadingLevel2 } =
        useGetLevelsByDispositionIdQuery(selectedLevel1!, {
            skip: !selectedLevel1 || !showLevel2,
        });
    const { data: level3, isLoading: isLoadingLevel3 } =
        useGetLevelsByDispositionIdQuery(selectedLevel2!, {
            skip: !selectedLevel2 || !showLevel3,
        });

    return (
        <Stack gap={1.5}>
            <RHFSelect
                name="ai_disposition"
                className="w-full nodrag nopan"
                variant="outlined"
                size="small"
                label="Select AI Disposition"
                onChangeCallback={() => {
                    setShowLevel2(false);
                    setShowLevel3(false);
                    setValue("disposition_levels", {
                        level1: null,
                        level2: null,
                        level3: null,
                    });
                }}
                options={(aiDispositionTemplates || [])?.map((item) => ({
                    id: item.id,
                    label: item.name,
                }))}
            />

            <Typography variant="textSm" fontWeight={600}>
                Levels
            </Typography>

            <Controller
                name="disposition_levels.level1"
                control={control}
                render={({
                    field: { value, onChange },
                    fieldState: { error },
                }) => {
                    return (
                        <GenericSingleSelect<{ id: number; label: string }>
                            label="Select Level 1"
                            className="w-full"
                            loading={isLoadingLevel1}
                            autocompleteProps={{
                                openOnFocus: true,
                                options: (level1 || []).map((item) => ({
                                    id: item.id,
                                    label: item.name,
                                })),
                            }}
                            error={!!error}
                            disabled={!ai_disposition}
                            helperText={error?.message}
                            value={value}
                            setValue={(e) => {
                                onChange(e);
                                setShowLevel2(false);
                                setShowLevel3(false);
                                setValue("disposition_levels.level2", null);
                                setValue("disposition_levels.level3", null);
                            }}
                        />
                    );
                }}
            />
            {selectedLevel1 && !selectedLevel2 && (
                <div>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => setShowLevel2(true)}
                    >
                        <PlusIconSvg /> Add Level 2
                    </Button>
                </div>
            )}
            {showLevel2 && (
                <Controller
                    name="disposition_levels.level2"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <GenericSingleSelect<{ id: number; label: string }>
                            label="Select Level 2"
                            value={value}
                            loading={isLoadingLevel2}
                            setValue={(e) => {
                                onChange(e);
                                setShowLevel3(false);
                                setValue("disposition_levels.level3", null);
                            }}
                            autocompleteProps={{
                                options: (level2 || []).map((item) => ({
                                    id: item.id,
                                    label: item.name,
                                })),
                                openOnFocus: true,
                                disableClearable: false,
                            }}
                        />
                    )}
                />
            )}
            {selectedLevel2 && !selectedLevel3 && (
                <div>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => setShowLevel3(true)}
                    >
                        <PlusIconSvg /> Add Level 3
                    </Button>
                </div>
            )}
            {showLevel3 && (
                <Controller
                    name="disposition_levels.level3"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <GenericSingleSelect<{ id: number; label: string }>
                            label="Select Level 3"
                            value={value}
                            loading={isLoadingLevel3}
                            setValue={(e) => onChange(e)}
                            autocompleteProps={{
                                options: (level3 || []).map((item) => ({
                                    id: item.id,
                                    label: item.name,
                                })),
                                openOnFocus: true,
                                disableClearable: false,
                            }}
                        />
                    )}
                />
            )}
        </Stack>
    );
};

const Entity = ({
    methods,
    control,
}: {
    methods: UseFormReturn<AttributeBlockFormType>;
    control: Control<AttributeBlockFormType>;
}) => {
    const { toggleDrawerState } = useParameterAiConfigContext();
    const { data: entities, isFetching: isLoadingEntities } =
        useGetCustomEntitiesQuery();
    const { watch, setValue } = methods;
    const attribute_entity = watch("attribute_entity");
    const eligibleValues =
        entities?.find((e) => e.id === attribute_entity)?.metadata?.keywords ||
        [];

    return (
        <Stack gap={1.5}>
            <RHFSelectCustom<{ id: number; label: string }>
                name={"attribute_entity"}
                label="Choose Entity"
                onChangeCallback={() => {
                    setValue("eligible_entities", []);
                }}
                autocompleteProps={{
                    options:
                        entities
                            ?.filter((e) => e.metadata?.field_type === "list")
                            .map((e) => ({
                                id: e.id,
                                label: e.name,
                            })) ?? [],
                    size: "small",
                    loading: isLoadingEntities,
                    PaperComponent: ({ children }) => {
                        return (
                            <Paper
                                sx={(theme) => ({
                                    border: "1px solid",
                                    borderColor: alpha(
                                        theme.palette.textColors["333"],
                                        0.1
                                    ),
                                })}
                            >
                                <Box
                                    p={2}
                                    gap={1}
                                    onMouseDown={(event) =>
                                        event.preventDefault()
                                    }
                                    color="primary.main"
                                    fontWeight={600}
                                    component="button"
                                    onClick={() => {
                                        toggleDrawerState("isEntityModalOpen");
                                    }}
                                >
                                    + Create Entity
                                </Box>
                                {children}
                            </Paper>
                        );
                    },
                }}
            />
            {attribute_entity && (
                <Controller
                    name="eligible_entities"
                    control={control}
                    render={({
                        field: { value, onChange },
                        fieldState: { error },
                    }) => (
                        <GenericMultipleSelect<{ id: string; label: string }>
                            label="Select Eligible Values"
                            values={value ?? []}
                            className="w-full"
                            setValues={(e) => onChange(e)}
                            autocompleteProps={{
                                openOnFocus: true,
                                options: eligibleValues.map((item) => ({
                                    id: item,
                                    label: item,
                                })),
                            }}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />
            )}
        </Stack>
    );
};
